import React, { useEffect, useState } from 'react';
import { calendarApi } from './api';
import { addMinutes, minutesBetween } from './calendarFollowup';
import { CloseX, useCloseGuard } from './calendarModal';

// =============================================================================
// 5.38.0 "📅 Follow-up" from a client folder (main folder or any subfolder — it always belongs to the CLIENT).
// One dialog: Appointment | Task. Destination = a person's Personal calendar (appointment) or Tasks (task) — never
// Office. Default person = the clicking user; staff can only choose themselves, InsureDocs admins anyone with a
// Personal calendar (an admin without one must pick). The server enforces the same (403 / 400).
// Defaults (from GET /api/calendar/followups/defaults): title "Follow up: <client> (<#>)", appointment tomorrow
// 11:00 AM for 30 minutes with the 15-minute reminder, task due tomorrow (time optional). The note goes into the
// item's description with the client's link underneath (added by the server, with URL + X-INSUREDOCS-CLIENT-ID).
// 5.43.0: closes only by Cancel / the save button / ✕ (not by clicking outside); changes ask "Discard changes?".
// =============================================================================

const REMINDERS = [[null, 'No reminder'], [0, 'At start'], [5, '5 minutes before'], [10, '10 minutes before'],
  [15, '15 minutes before'], [30, '30 minutes before'], [60, '1 hour before'], [1440, '1 day before']];
const DURATIONS = [[15, '15 min'], [30, '30 min'], [45, '45 min'], [60, '1 hour'], [90, '1½ hours'], [120, '2 hours']];

export default function FollowupDialog({ client, onClose, onCreated, theme }) {
  const t = theme || {};
  const C = { bg: t.bg || '#fff', panel: t.panelBg || '#fff', border: t.borderColor || '#ddd', text: t.textPrimary || '#222', dim: t.textSecondary || '#666' };
  const [d, setD] = useState(null);            // defaults + targets
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [kind, setKind] = useState('event');
  const [who, setWho] = useState('');
  const [title, setTitle] = useState('');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('11:00');
  const [mins, setMins] = useState(30);
  const [reminder, setReminder] = useState(15);
  const [due, setDue] = useState('');
  const [dueTime, setDueTime] = useState('');
  const [note, setNote] = useState('');
  const [base, setBase] = useState(null);      // 5.43.0 the values the dialog opened with (to know if anything changed)

  useEffect(() => {
    let live = true;
    calendarApi.followupDefaults(client.id).then((r) => {
      if (!live) return;
      const x = r.data;
      setD(x);
      setWho(x.default || '');
      setTitle(x.title);
      setDate(x.start.slice(0, 10));
      setTime(x.start.slice(11, 16));
      setMins(minutesBetween(x.start, x.end) || 30);
      setReminder(x.reminder_minutes);
      setDue(x.due);
      setBase(JSON.stringify(['event', x.default || '', x.title, x.start.slice(0, 10), x.start.slice(11, 16), minutesBetween(x.start, x.end) || 30, x.reminder_minutes, x.due, '', '']));
    }).catch((e) => { if (live) setErr(e?.response?.data?.detail || 'Could not open the follow-up form'); });
    return () => { live = false; };
  }, [client.id]);

  const save = async () => {
    setErr('');
    if (!who) { setErr('Choose whose calendar this goes on'); return; }
    if (kind === 'event' && (!date || !time)) { setErr('Choose a date and time'); return; }
    const start = `${date}T${time}:00`;
    const body = { client_id: client.id, kind, username: who, title, note };
    if (kind === 'event') Object.assign(body, { start, end: addMinutes(start, Number(mins)), all_day: false, reminder_minutes: reminder });
    else body.due = due ? (dueTime ? `${due}T${dueTime}:00` : due) : null;
    setBusy(true);
    try {
      const r = await calendarApi.createFollowup(body);
      onCreated && onCreated(r.data);
      onClose();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Could not save the follow-up');
    } finally { setBusy(false); }
  };

  const dirty = base !== null && JSON.stringify([kind, who, title, date, time, mins, reminder, due, dueTime, note]) !== base;
  const { requestClose, prompt } = useCloseGuard(dirty, onClose);

  const input = { padding: '7px 9px', border: `1px solid ${C.border}`, borderRadius: 6, background: C.bg, color: C.text, fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box', colorScheme: t.darkMode ? 'dark' : 'light' };
  const label = { fontSize: 12, color: C.dim, display: 'block', marginBottom: 3 };
  const seg = (on) => ({ flex: 1, padding: '7px 10px', border: 'none', background: on ? '#7c3aed' : 'transparent', color: on ? '#fff' : C.text, fontSize: 13, fontWeight: 600, cursor: 'pointer' });
  const others = d && d.choices.length > 1;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div role="dialog" aria-label="Follow-up" data-testid="followup-dialog"
        style={{ background: C.panel, color: C.text, border: `1px solid ${C.border}`, borderRadius: 10, width: 460, maxWidth: '94vw', maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 12px 32px rgba(0,0,0,0.35)' }}>
        <div style={{ padding: '12px 16px', borderBottom: `1px solid ${C.border}`, fontWeight: 700, fontSize: 15, display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ flex: 1 }}>📅 Follow-up — {client.name}</span>
          <CloseX onClick={requestClose} disabled={busy} testid="followup-x" color={C.dim} />
        </div>
        <div style={{ padding: 16, display: 'grid', gap: 12 }}>
          <div style={{ display: 'flex', border: '1px solid #7c3aed', borderRadius: 7, overflow: 'hidden' }}>
            <button type="button" style={seg(kind === 'event')} onClick={() => setKind('event')} data-testid="followup-kind-event">Appointment</button>
            <button type="button" style={seg(kind === 'task')} onClick={() => setKind('task')} data-testid="followup-kind-task">Task</button>
          </div>
          {!d && !err && <div style={{ color: C.dim, fontSize: 13 }}>Loading…</div>}
          {d && (
            <>
              <div>
                <span style={label}>{kind === 'event' ? 'Calendar' : 'Task list'}</span>
                {others ? (
                  <select value={who} onChange={(e) => setWho(e.target.value)} style={{ ...input, width: '100%' }} data-testid="followup-who">
                    {!d.default && <option value="">— Choose a person —</option>}
                    {d.choices.map((c) => <option key={c.username} value={c.username}>{c.full_name}{c.username === d.default ? ' (me)' : ''}</option>)}
                  </select>
                ) : (
                  <div style={{ fontSize: 13 }} data-testid="followup-who-fixed">{d.choices[0] ? `${d.choices[0].full_name} — ${kind === 'event' ? 'Personal' : 'Tasks'}` : '—'}</div>
                )}
              </div>
              <div>
                <span style={label}>Title</span>
                <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={500} style={{ ...input, width: '100%' }} data-testid="followup-title" />
              </div>
              {kind === 'event' ? (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                  <div><span style={label}>Date</span><input type="date" value={date} onChange={(e) => setDate(e.target.value)} style={{ ...input, width: '100%' }} data-testid="followup-date" /></div>
                  <div><span style={label}>Time</span><input type="time" value={time} onChange={(e) => setTime(e.target.value)} style={{ ...input, width: '100%' }} data-testid="followup-time" /></div>
                  <div><span style={label}>Length</span>
                    <select value={mins} onChange={(e) => setMins(Number(e.target.value))} style={{ ...input, width: '100%' }} data-testid="followup-length">
                      {DURATIONS.map(([m, l]) => <option key={m} value={m}>{l}</option>)}
                    </select>
                  </div>
                  <div style={{ gridColumn: '1 / span 3' }}><span style={label}>Reminder</span>
                    <select value={reminder === null ? '' : reminder} onChange={(e) => setReminder(e.target.value === '' ? null : Number(e.target.value))} style={{ ...input, width: '100%' }} data-testid="followup-reminder">
                      {REMINDERS.map(([m, l]) => <option key={String(m)} value={m === null ? '' : m}>{l}</option>)}
                    </select>
                  </div>
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  <div><span style={label}>Due date</span><input type="date" value={due} onChange={(e) => setDue(e.target.value)} style={{ ...input, width: '100%' }} data-testid="followup-due" /></div>
                  <div><span style={label}>Time (optional)</span><input type="time" value={dueTime} onChange={(e) => setDueTime(e.target.value)} style={{ ...input, width: '100%' }} data-testid="followup-due-time" /></div>
                </div>
              )}
              <div>
                <span style={label}>Note</span>
                <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={4} placeholder="What to follow up on"
                  style={{ ...input, width: '100%', resize: 'vertical' }} data-testid="followup-note" />
                <div style={{ fontSize: 11, color: C.dim, marginTop: 3 }}>The client's link is added under the note: {d.link}</div>
              </div>
            </>
          )}
          {err && <div style={{ background: '#7f1d1d', color: '#fecaca', padding: '7px 10px', borderRadius: 6, fontSize: 13 }} data-testid="followup-error">{err}</div>}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button type="button" onClick={requestClose} data-testid="followup-cancel" style={{ padding: '7px 14px', border: `1px solid ${C.border}`, borderRadius: 6, background: 'transparent', color: C.text, cursor: 'pointer' }}>Cancel</button>
            <button type="button" onClick={save} disabled={busy || !d} data-testid="followup-save"
              style={{ padding: '7px 16px', border: 'none', borderRadius: 6, background: '#7c3aed', color: '#fff', fontWeight: 600, cursor: 'pointer', opacity: busy || !d ? 0.6 : 1 }}>
              {busy ? 'Saving…' : kind === 'event' ? 'Add appointment' : 'Add task'}
            </button>
          </div>
        </div>
      </div>
      {prompt}
    </div>
  );
}
